import { formatDateTime } from '@shared/format';
import { useDataset } from '../hooks/useDataset';
import { ExternalLink } from '../components/ExternalLink';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { pageTitle, SITE_EMAIL, type PageConfig } from '../site';
import styles from './AboutPage.module.css';

interface Props {
    page: PageConfig;
}

export function AboutPage({ page }: Props) {
    const { dataset } = useDataset();
    useDocumentTitle(pageTitle(page));
    return (
        <div className={`container container--narrow ${styles.about}`}>
            <h1>À propos – Mentions légales</h1>
            <p>Ce site a pour but d'aider les randonneurs et les traileurs de l'Île de la Réunion.</p>
            <p>
                Si vous souhaitez faire figurer, modifier ou supprimer des informations sur ce site, n'hésitez pas à{' '}
                <ExternalLink href={`mailto:${SITE_EMAIL}`}>nous contacter</ExternalLink>. Nous le ferons avec plaisir.
            </p>

            <h2>Sécurité sur les sentiers</h2>
            <aside className={`${styles.callout} ${styles.info}`}>
                <p className={styles.calloutTitle}>
                    Nous nous efforçons de mettre à jour les données de l'ONF chaque jour
                </p>
                <p>
                    La dernière mise à jour a été effectuée le{' '}
                    <b className="last-onf-update">{dataset ? formatDateTime(dataset.lastUpdate) : '…'}</b>
                </p>
            </aside>
            <p>
                Si vous constatez des données incorrectes, faites le nous savoir par email : <code>{SITE_EMAIL}</code>.
            </p>
            <p>
                <b>La randonnée à La Réunion nécessite une bonne préparation.</b> Nous vous invitons fortement à
                consulter{' '}
                <ExternalLink href="https://www.onf.fr/vivre-la-foret/+/5f6::ile-de-la-reunion-guide-conseils-aux-randonneurs.html">
                    le livret Conseil aux randonneurs à La Réunion
                </ExternalLink>{' '}
                élaboré par l'ONF et ses partenaires.
            </p>
            <p>
                Le bon sens doit toujours primer : ne vous engagez pas sur un sentier si vous avez un doute sur son
                état. Les sentiers fermés sont généralement signalés par un affichage spécifique au début de
                l'itinéraire.{' '}
                <b>Ne vous engagez pas si le sentier est fermé, même si la rubalise n'est plus en place</b>. Il en va de
                votre sécurité.
            </p>

            <h2>Sources des données</h2>
            <aside className={`${styles.callout} ${styles.warning}`}>
                <p className={styles.calloutTitle}>Le contenu de ce site n'est pas encore réutilisable librement</p>
                <p>Nous y travaillons. N'hésitez pas à revenir bientôt pour vérifier !</p>
            </aside>
            <p>Les données présentes sur Tipa.re proviennent de plusieurs sources :</p>
            <ul>
                <li>
                    La position des points de départs et d'arrivée et les points intermédiaires (non affichés sur la
                    carte) ont été collectées manuellement par Tipa.re
                    <ul>
                        <li>Pas de réutilisation autorisée pour le moment</li>
                    </ul>
                </li>
                <li>
                    Les traces d'itinéraires proviennent du{' '}
                    <ExternalLink href="https://geoservices.ign.fr/documentation/services/services-geoplateforme/itineraire">
                        calculateur d'itinéraire de l'IGN
                    </ExternalLink>
                    .
                    <ul>
                        <li>
                            License{' '}
                            <ExternalLink href="https://etalab.gouv.fr/licence-ouverte-open-licence">
                                Etalab 2.0
                            </ExternalLink>
                        </li>
                    </ul>
                </li>
                <li>
                    Les courbes d'élévation proviennent du{' '}
                    <ExternalLink href="https://geoservices.ign.fr/documentation/services/services-geoplateforme/altimetrie">
                        service de calcul altimétrique de l'IGN
                    </ExternalLink>
                    .
                    <ul>
                        <li>
                            License{' '}
                            <ExternalLink href="https://etalab.gouv.fr/licence-ouverte-open-licence">
                                Etalab 2.0
                            </ExternalLink>
                        </li>
                    </ul>
                </li>
                <li>
                    Les données d'ouverture et de fermeture des sentiers proviennent de la{' '}
                    <ExternalLink href="https://www.onf.fr/vivre-la-foret/+/b90::randonnee-la-reunion-connaitre-les-sentiers-fermes.html">
                        carte de l'ONF
                    </ExternalLink>
                    <ul>
                        <li>
                            <b>
                                Tipa.re souhaite accompagner l'ONF à rendre ces données facilement accessibles, dans une
                                license OpenData
                            </b>
                        </li>
                        <li>
                            Les arrêtés préfectoraux, dans le domaine public, sont disponibles sur{' '}
                            <ExternalLink href="https://www.reunion.gouv.fr/Publications/Publications-administratives-et-legales">
                                la page des publications administratives de la Préfecture de la Réunion
                            </ExternalLink>
                        </li>
                    </ul>
                </li>
                <li>
                    Les données présentes sur la carte proviennent du projet{' '}
                    <ExternalLink href="https://www.openstreetmap.org/">OpenStreetMap</ExternalLink>
                    <ul>
                        <li>
                            Les données OpenStreetMap sont sous{' '}
                            <ExternalLink href="https://www.openstreetmap.org/copyright">licence ODbL</ExternalLink>
                        </li>
                        <li>
                            Le fond de carte est dessiné par{' '}
                            <ExternalLink href="https://maplibre.org/">MapLibre</ExternalLink> à partir des tuiles{' '}
                            <ExternalLink href="https://protomaps.com/">Protomaps</ExternalLink> (extrait quotidien
                            d'OpenStreetMap)
                        </li>
                    </ul>
                </li>
                <li>
                    Le relief de la carte (ombrage, courbes de niveau, vue 3D) provient du{' '}
                    <ExternalLink href="https://geoservices.ign.fr/lidarhd">
                        modèle numérique de terrain LiDAR HD de l'IGN
                    </ExternalLink>{' '}
                    (précision 50 cm)
                    <ul>
                        <li>
                            License{' '}
                            <ExternalLink href="https://etalab.gouv.fr/licence-ouverte-open-licence">
                                Etalab 2.0
                            </ExternalLink>
                        </li>
                    </ul>
                </li>
            </ul>
            <div className={styles.logos}>
                <ExternalLink href="https://www.ign.fr/">
                    <img src="/assets/logo-ign.svg" alt="Logo IGN" />
                </ExternalLink>
                <ExternalLink href="https://www.onf.fr/">
                    <img src="/assets/logo-onf.svg" alt="Logo ONF" />
                </ExternalLink>
                <ExternalLink href="https://www.openstreetmap.org/">
                    <img src="/assets/logo-openstreetmap.svg" alt="Logo OpenStreetMap" />
                </ExternalLink>
            </div>
            <ul>
                <li>Les logos et marques reproduites sont propriété de leurs détenteurs respectifs.</li>
            </ul>

            <h2>Mentions légales</h2>
            <p>
                Ce site est édité par la société{' '}
                <ExternalLink href="https://www.sys-dev-run.fr/">SysDevRun</ExternalLink>. Théophile Helleboid en est le
                directeur de la publication.
            </p>
            <p>
                SysDevRun est établie au 112 rue Pablo Neruda, 97419 La Possession, à la Réunion. Vous pouvez le
                contacter par email : <code>{SITE_EMAIL}</code>. L'hébergeur du site est la société{' '}
                <ExternalLink href="https://about.gitlab.com/">Gitlab.com</ExternalLink>.
            </p>
            <p>
                L'entreprise SysDevRun est une EURL au capital social de 4 000 €, immatriculée au Registre du Commerce
                et des Sociétés de Saint-Denis sous le numéro 900 998 014.
            </p>

            <h2>Données personnelles</h2>
            <aside className={`${styles.callout} ${styles.info}`}>
                <p className={styles.calloutTitle}>Nous ne collectons pas de données personnelles.</p>
                <p>Et il n'y a aucune publicité</p>
            </aside>
            <p>
                Si vous le souhaitez, vous pouvez tout de même joindre le responsable à la protection des données du
                site avec les coordonnées présentes sur cette page.
            </p>
        </div>
    );
}
